// Unit conversions & formatting helpers. Backend always returns metric (°C, m/s, meters).

export const cToF = (c) => Math.round((c * 9) / 5 + 32);

export const temp = (c, units) =>
  units === "imperial" ? cToF(c) : Math.round(c);

export const tempUnit = (units) => (units === "imperial" ? "°F" : "°C");

export const speed = (ms, units) =>
  units === "imperial" ? Math.round(ms * 2.23694) : Math.round(ms * 3.6);

export const speedUnit = (units) => (units === "imperial" ? "mph" : "km/h");

export const visibility = (m, units) =>
  units === "imperial"
    ? `${(m / 1609.34).toFixed(1)} mi`
    : `${(m / 1000).toFixed(1)} km`;

const DAYS = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const DAYS_FULL = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

const localDate = (dt, offset) => new Date((dt + offset) * 1000);

export const formatHour = (dt, offset) => {
  const d = localDate(dt, offset);
  return `${String(d.getUTCHours()).padStart(2, "0")}:00`;
};

export const formatDay = (dt, offset, i) => {
  if (i === 0) return "Hari Ini";
  const d = localDate(dt, offset);
  return DAYS[d.getUTCDay()];
};

export const formatDayFull = (dt, offset) => {
  const d = localDate(dt, offset);
  return `${DAYS_FULL[d.getUTCDay()]}, ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
};

export const formatClock = (dt, offset) => {
  const d = localDate(dt, offset);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(
    d.getUTCMinutes()
  ).padStart(2, "0")}`;
};

export const isDaytime = (offset, sunrise, sunset, now) => {
  const t = now || Date.now() / 1000;
  return t >= sunrise && t <= sunset;
};

// Weather condition -> accent theme
export const conditionTheme = (main, icon) => {
  const night = icon && icon.endsWith("n");
  switch (main) {
    case "Clear":
      return night
        ? { label: "clear-night", accent: "#6366F1", glow: "rgba(99,102,241,0.28)" }
        : { label: "sunny", accent: "#F59E0B", glow: "rgba(245,158,11,0.26)" };
    case "Clouds":
      return { label: "cloudy", accent: "#64748B", glow: "rgba(100,116,139,0.24)" };
    case "Rain":
    case "Drizzle":
      return { label: "rain", accent: "#3B82F6", glow: "rgba(59,130,246,0.30)" };
    case "Thunderstorm":
      return { label: "storm", accent: "#8B5CF6", glow: "rgba(139,92,246,0.32)" };
    case "Snow":
      return { label: "snow", accent: "#38BDF8", glow: "rgba(56,189,248,0.28)" };
    default:
      return { label: "mist", accent: "#94A3B8", glow: "rgba(148,163,184,0.24)" };
  }
};

// OpenWeather AQI 1..5
export const aqiInfo = (aqi) => {
  const map = {
    1: { label: "Baik", color: "#22C55E", desc: "Kualitas udara memuaskan" },
    2: { label: "Sedang", color: "#EAB308", desc: "Dapat diterima" },
    3: { label: "Tidak Sehat (Sensitif)", color: "#F97316", desc: "Kurang baik bagi kelompok sensitif" },
    4: { label: "Tidak Sehat", color: "#EF4444", desc: "Berdampak pada kesehatan" },
    5: { label: "Sangat Tidak Sehat", color: "#A855F7", desc: "Peringatan kesehatan darurat" },
  };
  return map[aqi] || map[1];
};

export const uvInfo = (uvi) => {
  if (uvi < 3) return { label: "Rendah", color: "#22C55E" };
  if (uvi < 6) return { label: "Sedang", color: "#EAB308" };
  if (uvi < 8) return { label: "Tinggi", color: "#F97316" };
  if (uvi < 11) return { label: "Sangat Tinggi", color: "#EF4444" };
  return { label: "Ekstrem", color: "#A855F7" };
};

export const windDirection = (deg) => {
  const dirs = ["U", "TL", "T", "TG", "S", "BD", "B", "BL"];
  return dirs[Math.round(deg / 45) % 8];
};

export const locationDetail = (loc) => {
  const parts = [];
  [loc.district, loc.regency, loc.state].forEach((p) => {
    if (p && p !== loc.name && !parts.includes(p)) parts.push(p);
  });
  return parts.join(", ");
};

export const locationLabel = (loc) =>
  locationDetail(loc) ? loc.name : [loc.name, loc.country].filter(Boolean).join(", ");
